import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import api from "../lib/api";
import Modal from "./Modal";
import VoiceTextarea from "./VoiceTextarea";
import { useBranding } from "../context/TenantBrandingContext";

/**
 * Fetches pending Hidro reflections (post_win, post_loss, weekly_review) for
 * the current user and shows the OLDEST one as a dismissable modal.
 *
 * Mounts once on the Dashboard. Users can answer or dismiss; either way the
 * doc is removed from the pending list.
 *
 * Wired to `/api/hidro/reflections/pending`, `/answer`, `/dismiss`.
 */
export default function HidroReflectionPrompt() {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [ref, setRef] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hidro/reflections/pending");
        if (Array.isArray(data) && data.length > 0) {
          setRef(data[data.length - 1]); // oldest
        }
      } catch (_e) { /* noop */ }
    })();
  }, []);

  const submit = async () => {
    if (!ref || !answer.trim()) return;
    setBusy(true);
    try {
      await api.post(`/hidro/reflections/${ref.id}/answer`, { answer: answer.trim() });
      setRef(null);
      setAnswer("");
    } catch (_e) { /* noop */ }
    setBusy(false);
  };

  const dismiss = async () => {
    if (!ref) return;
    setBusy(true);
    try {
      await api.post(`/hidro/reflections/${ref.id}/dismiss`);
      setRef(null);
    } catch (_e) { /* noop */ }
    setBusy(false);
  };

  if (!ref) return null;

  const badge =
    ref.kind === "post_win"
      ? { label: "Post-win reflection", color: "#34d399" }
      : ref.kind === "post_loss"
      ? { label: "Post-loss reflection", color: "#f87171" }
      : { label: "Weekly review", color: "#1fb6c1" };

  return (
    <Modal
      open={!!ref}
      onClose={dismiss}
      title={`${aiName} wants a quick moment`}
      testId="hidro-reflection-modal"
    >
      <div className="space-y-3 pt-1" data-testid="hidro-reflection-body">
        <div
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
          style={{
            color: badge.color,
            borderColor: `${badge.color}55`,
            background: `${badge.color}10`,
          }}
          data-testid="hidro-reflection-kind"
        >
          <Sparkles className="w-3 h-3" /> {badge.label}
        </div>
        <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="hidro-reflection-question">
          {ref.question}
        </div>
        <VoiceTextarea
          value={answer}
          onChange={setAnswer}
          rows={3}
          placeholder="One sentence — I'll add it to your playbook so future briefs get sharper."
          className="text-sm w-full"
          context="hidro_reflection"
          testIdPrefix="hidro-reflection"
          data-testid="hidro-reflection-answer"
          autoFocus
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="hidro-reflection-dismiss"
          >
            <X className="w-3.5 h-3.5" /> skip
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !answer.trim()}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="hidro-reflection-submit"
          >
            {busy ? "saving…" : "save to playbook"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
